/**
 * src/engine/engineEventBus.js
 *
 * Tiny synchronous pub/sub for engine events.
 * useAppState emits events → useEngine listens and reacts.
 *
 * Events:
 *   document.saved                 payload: { doc }
 *   document.deleted               payload: { docId }
 *   task.saved                     payload: { task }
 *   task.evidence_added            payload: { task }
 *   risk.saved                     payload: { risk }
 *   vendor.saved                   payload: { vendor }
 *   vendor.subprocessors_changed   payload: { vendor, priorSubProcessors, newSubProcessors }
 *
 * Activity-log action types emitted by server-side processes (no client
 * listener; documented here so the canonical list stays in one file):
 *   vulnerability.detected         server-side correlator — per new finding
 *   vulnerability.tier_changed     reserved (Phase 5) — emitted when a
 *                                  finding's tier changes between runs
 *   vulnerability.suppressed       client UI — admin suppressed a finding
 *   vulnerability.accepted         client UI — admin accepted a finding
 *   vulnerability.deferred         client UI — admin deferred a finding
 *   vulnerability.actor_overridden client UI — admin set actor=internal/external
 *   vulnerability.assigned         client UI — admin assigned a finding
 *   vulnerability.promoted_to_risk client UI — admin promoted vuln to Risk register
 *   asset.collision_detected       server-side correlator — namespaced
 *                                  identifier collision flagged (AST-B-004)
 */

const listeners = {};

export function emitEngineEvent(eventName, payload = {}) {
  const fns = listeners[eventName] || [];
  fns.forEach((fn) => {
    try {
      fn(payload);
    } catch (e) {
      console.warn('[engineEventBus] listener error:', e);
    }
  });
}

export function onEngineEvent(eventName, fn) {
  if (!listeners[eventName]) listeners[eventName] = [];
  listeners[eventName].push(fn);
  // return unsubscribe fn
  return () => {
    listeners[eventName] = listeners[eventName].filter((f) => f !== fn);
  };
}
